import React, { useState } from 'react';
import Page from '../components/Page';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Grid,
  Typography,
} from '@mui/material';
import { AppColors, typographyClamps } from '../utils/utils';
import { BenefitSectionView } from '../components/BenefitSectionView';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

interface Feature {
  iconPath: string;
  title?: string;
  description?: string;
  subtitle1?: string;
  subtitle2?: string;
}

const featureCards: Feature[] = [
  { iconPath: '/static/benefits_page/img_benefits_tile_1.png' },
  { iconPath: '/static/benefits_page/img_benefits_tile_2.png' },
  { iconPath: '/static/benefits_page/img_benefits_tile_3.png' },
  { iconPath: '/static/benefits_page/img_benefits_tile_4.png' },
  { iconPath: '/static/benefits_page/img_benefits_tile_5.png' },
  { iconPath: '/static/benefits_page/img_benefits_tile_6.png' },
  { iconPath: '/static/benefits_page/img_benefits_tile_7.png' },
  { iconPath: '/static/benefits_page/img_benefits_tile_8.png' },
  { iconPath: '/static/benefits_page/img_benefits_tile_9.png' },
  { iconPath: '/static/benefits_page/img_benefits_tile_10.png' },
];

interface FAQSection {
  title: string;
  questions: FAQItem[];
}
interface FAQItem {
  question: string;
  answer: string;
}

const faqData: FAQSection[] = [
  {
    title: '1️⃣ FUNCTIONALITY & FEATURES',
    questions: [
      {
        question: 'How does QR Buddy actually identify the shopper?',
        answer:
          'When a customer scans the QR Buddy code, a one-tap login screen (Facebook, Apple, or Google) verifies their identity instantly. That means the dealer gets their name, phone number, and vehicle interest immediately — no forms and no friction.',
      },

      {
        question: 'Does the customer need to download an app?',
        answer:
          'No. QR Buddy is 100% mobile-web. The customer just scans and engages — no downloads, no accounts, no hassle.',
      },
      {
        question: 'What does the customer see when they scan?',
        answer:
          'They see the exact vehicle they are standing in front of: full photos, pricing, options, comparisons, and an AI assistant that can answer questions in real time.',
      },
      {
        question: 'What happens if the shopper views multiple vehicles?',
        answer:
          'All scan activity is tied to the same verified customer. The dealer sees exactly which vehicles they scanned, favorited, and compared — giving immediate insight into buyer intent.',
      },
      {
        question: 'How does the AI help customers?',
        answer:
          'Our AI answers questions instantly (features, mileage, availability), helps schedule test drives, and can push buyers into starting a finance app — even when reps aren’t available.',
      },
    ],
  },
  {
    title: '2️⃣ LEADS, PASS-OFF & COMMUNICATION',
    questions: [
      {
        question: 'How do salespeople get leads?',
        answer:
          'The moment a customer scans and logs in, the assigned rep receives a real-time alert with the shopper’s name, phone number, and the exact vehicle they’re interacting with.',
      },
      {
        question: 'What if no rep is available?',
        answer:
          'QR Buddy’s AI keeps the conversation alive: answering questions, confirming interest, and booking next-day appointments — so the lead never goes cold.',
      },
      {
        question: 'Are these internet leads or walk-in leads?',
        answer:
          'These are high-intent walk-ups and real shoppers — customers physically on the lot or browsing inventory online. It’s the highest-quality lead source a dealership can have.',
      },
      {
        question: 'Can these leads be pushed into our CRM?',
        answer:
          'Yes. Leads, activity, and engagement data can be exported at any time and imported directly into your CRM.',
      },
    ],
  },
  {
    title: '3️⃣ PRICING & BILLING',
    questions: [
      {
        question: 'What does the Pay-As-You-Go plan include?',
        answer:
          'Pay-As-You-Go captures after-hours leads from 6 PM to 10 AM for $40 per verified customer. No monthly fees, no long contracts — perfect for starting small.',
      },
      {
        question: 'What does the Subscription Plan include?',
        answer:
          'Our 90-day subscription (largest plan we offer):\n• $1,500 monthly platform access\n• $10 per customer lead (not per scan)\n• Full AI coverage, 24/7\n• Full analytics & reporting\n• Real-time dealer dashboard',
      },
      {
        question: 'Why only a 90-day contract?',
        answer:
          'Because QR Buddy is built on performance instead of long-term commitments. If it doesn’t produce, you’re not stuck.',
      },
      {
        question: 'Are setup fees required?',
        answer:
          'Yes. $1,495 one-time setup covers your dashboard, QR system activation, and onboarding.',
      },
    ],
  },
  {
    title: '4️⃣ SETUP, SIGNAGE & INVENTORY',
    questions: [
      {
        question: 'How do we get our vehicles into QR Buddy?',
        answer:
          'Simply upload your inventory CSV. QR Buddy automatically generates your QR codes and 5×7 window signage for every vehicle — instantly.',
      },
      {
        question: 'Do you provide promotional signage for the lot?',
        answer:
          'Yes. Dealers can order window stickers, table tents, and lot signs directly from the QR Buddy store — usually shipped within 2 business days.',
      },
      {
        question: 'How long does full setup take?',
        answer:
          'About 24 hours for activation + uploading inventory + signage placement. Dealers can literally be live the next day.',
      },
    ],
  },
];

const Benefits: React.FC = () => {
  // const theme = useTheme();
  // const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // e.g. <600px
  // const isMobileOrTablet = useMediaQuery(theme.breakpoints.down('md')); // if screen <= 'ms' for tablet and mobile device

  const [expanded, setExpanded] = useState<string | false>(false);

  const handleChange =
    (panel: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
    };

  // const scrollRef = useRef<HTMLDivElement>(null);

  // const scroll = (direction: 'left' | 'right') => {
  //   const scrollAmount = 300; // adjust as needed
  //   const container = scrollRef.current;

  //   if (!container) return;

  //   const maxScrollLeft = 0;
  //   const maxScrollRight = container.scrollWidth - container.clientWidth;

  //   if (direction === 'left') {
  //     // Only scroll left if not already at the start
  //     if (container.scrollLeft > maxScrollLeft) {
  //       const newScrollLeft = Math.max(
  //         container.scrollLeft - scrollAmount,
  //         maxScrollLeft
  //       );
  //       container.scrollTo({
  //         left: newScrollLeft,
  //         behavior: 'smooth',
  //       });
  //     }
  //   } else if (direction === 'right') {
  //     // Only scroll right if not already at the end
  //     if (container.scrollLeft < maxScrollRight) {
  //       const newScrollLeft = Math.min(
  //         container.scrollLeft + scrollAmount,
  //         maxScrollRight
  //       );
  //       container.scrollTo({
  //         left: newScrollLeft,
  //         behavior: 'smooth',
  //       });
  //     }
  //   }
  // };

  return (
    <>
      <Page title={'Benefits'}>
        <Box
          sx={{
            position: 'relative',
            overflow: 'hidden',
            // maxHeight: isPlayIntroVideo ? '100%' : 'auto',
            background: `url('/static/features_page/img_features_banner_background.png')`,
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            px: '5%',
            py: '2%',
          }}
        >
          {/* <Box
                    component='img'
                    src='/static/images/ai_gradient_left.svg'
                    sx={{
                      position: 'absolute',
                      left: { xs: -30, md: 0 },
                      top: { xs: 0, md: -170 },
                      height: { xs: '40%', sm: '30%', md: 'auto' },
                      width: { xs: '40%', sm: '30%', md: 'auto' },
                    }}
                  />
        
                  <Box
                    component='img'
                    src='/static/images/ai_gradient_right.svg'
                    sx={{
                      position: 'absolute',
                      height: { xs: '60%', sm: '30%', md: 'auto' },
                      width: { xs: '60%', sm: '30%', md: 'auto' },
                      right: 0,
                      bottom: { xs: 0, md: -150 },
                    }}
                  /> */}

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
              alignItems: 'center',
              p: { xs: 3, md: 10 },
              background: `url('/static/features_page/img_features_banner.png')`,
              backgroundSize: '100% 100%',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center',
              borderRadius: { xs: '10px', sm: '20px', md: '25px' },
              gap: '10%',
              position: 'relative',
              overflow: 'clip',
            }}
          >
            <Box
              sx={{
                backgroundColor: '#00000080',

                width: '100%',
                height: '100%',
                position: 'absolute',
              }}
            />

            <Box
              component={'img'}
              src='/static/images/qr_buddy_logo_qr.png'
              sx={{
                height: { xs: '70px', sm: '80px', md: '120px' },
                zIndex: 1,
              }}
            />

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 1,
              }}
            >
              <Box
                component='img'
                src='/static/images/qr_buddy_text_logo.png'
                alt='QR Buddy'
                sx={{
                  height: { xs: '5vh', sm: '8vh' },
                }}
              />

              <Typography
                sx={{
                  fontSize: { xs: '1.3rem', md: '2.125rem' },
                  fontWeight: 'bold',
                  color: 'white',
                  mt: 0.5,
                }}
              >
                Benefits
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Box
            component={'img'}
            src='/static/benefits_page/img_benefits_banner.png'
            alignSelf={'center'}
            width={'100%'}
            // maxHeight={'100vh'}
          />
        </Box>

        <BenefitSectionView
          idx={0}
          label=''
          title={`Customer scans the QR code from each vehicle he's interested in; system generates a timestamp to identify whether lead was captured during or after business hours.`}
          subtitle=''
          features={[]}
          footerText=''
          imagePath={'/static/benefits_page/img_benefits_step_1.png'}
        />

        <BenefitSectionView
          idx={1}
          label=''
          title={`Scan opens QR Buddy web app and enables customer to quickly create account using Apple, Facebook or Google credentials.`}
          subtitle=''
          features={[]}
          footerText=''
          imagePath={'/static/benefits_page/img_benefits_step_2.png'}
        />

        <BenefitSectionView
          idx={2}
          label=''
          title={`QR Buddy's simple interface enables customer to view detailed info on each scanned vehicle, create list of favorites, view history, and compare vehicles.`}
          subtitle=''
          features={[]}
          footerText=''
          imagePath={'/static/benefits_page/img_benefits_step_3.png'}
        />

        <BenefitSectionView
          idx={3}
          label=''
          title={`Dealership receives qualified, real-time leads which are immediately distributed to sales person via round-robin, manual assignment, or AI based algorithm.`}
          subtitle=''
          features={[]}
          footerText=''
          imagePath={'/static/benefits_page/img_benefits_step_4.png'}
        />

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            background: `url('/static/benefits_page/img_benefits_tile_bg_big.png')`,
            // backgroundRepeat: 'repeat-y',
            backgroundSize: '100% auto',
            // background: AppColors.aiGradient,
            px: { xs: '5%', sm: '10%' },
            py: 4,
            position: 'relative',
          }}
        >
          <Grid container gap={4} justifyContent={'center'}>
            {featureCards.map((item) => {
              return (
                <Grid
                  size={{ xs: 12, sm: 5.5, xl: 3.6 }}
                  key={item.title}
                  display={'flex'}
                >
                  <Box
                    component={'img'}
                    src={item.iconPath}
                    sx={{
                      width: '100%',
                    }}
                  />
                </Grid>
              );
            })}
          </Grid>
        </Box>

        <Box maxWidth='800px' mx='auto' mt={4} px={2}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography
              fontSize={typographyClamps.h4}
              align='center'
              // gutterBottom
              color={AppColors.vividBlueColor}
            >
              Frequently Asked Questions
            </Typography>

            <Box
              component={'img'}
              src='/static/images/img_ai_robot_trimmed.png'
              sx={{
                height: { xs: '70px', ms: '120px' },
              }}
            />
          </Box>

          {faqData.map((faq, parentIndex) => (
            <Box>
              <Typography
                fontSize={typographyClamps.h6}
                fontWeight={600}
                // align='center'
                gutterBottom
                color={AppColors.textBlackColor}
                mt={4}
                // mb={2}
              >
                {faq.title}
              </Typography>
              {faq.questions.map((item, index) => (
                <Accordion
                  key={index}
                  expanded={expanded === `panel-${parentIndex}-${index}`}
                  onChange={handleChange(`panel-${parentIndex}-${index}`)}
                  disableGutters
                  sx={{
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                    // borderTop: index === 0 ? '1px solid #e0e0e0' : 'none',
                    borderBottom: '1px solid #e0e0e0',
                  }}
                >
                  <AccordionSummary
                    expandIcon={
                      <ExpandMoreIcon
                        sx={{ color: AppColors.vividBlueColor }}
                      />
                    }
                  >
                    <Typography
                      sx={{
                        color: AppColors.vividBlueColor,
                        fontWeight: 600,
                        fontSize: '1rem',
                      }}
                    >
                      {item.question}
                    </Typography>
                  </AccordionSummary>

                  <AccordionDetails>
                    <Typography
                      sx={{
                        color: AppColors.darkGrayColor,
                        fontSize: '0.95rem',
                        lineHeight: 1.6,
                      }}
                    >
                      {item.answer}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          ))}
        </Box>
      </Page>
    </>
  );
};

export default Benefits;
